import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, Plus, Minus, Edit, Home, ChevronRight, Search, Loader2, 
  RefreshCw, Trash2, AlertTriangle, Boxes, CircleDot, Save, Wrench, Download, Settings, X, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import ToolAndDie from '@/components/ToolAndDie';
import MachiningConsumables from '@/components/MachiningConsumables';
import BookToWOModal from '@/components/BookToWOModal';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HEAT_TREATMENT_CONDITIONS = ['Raw', 'Annealed', 'Normalized', 'Hardened', 'Tempered'];
const STOCK_CATEGORIES = ['Finished Goods', 'Components', 'Consumables', 'Tools', 'Packaging', 'Other'];
const STOCK_UNITS = ['pcs', 'kg', 'm', 'sets', 'boxes', 'liters'];

const Inventory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('raw-materials');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Raw Materials state
  const [inventory, setInventory] = useState([]);
  const [rawDialogOpen, setRawDialogOpen] = useState(false);
  const [editingRaw, setEditingRaw] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedRawItem, setSelectedRawItem] = useState(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [rawFormData, setRawFormData] = useState({
    material_grade: '',
    diameter: '',
    length: '',
    cast_number: '',
    unit_cost: '',
    heat_treatment_condition: 'Raw',
    quantity_available: '',
    unit: 'bars'
  });

  const calcBilletWeight = (dia, len) => {
    const d = parseFloat(dia) || 0;
    const l = parseFloat(len) || 0;
    if (d <= 0 || l <= 0) return 0;
    return Math.round(Math.PI * (d * d / 4) * l * 0.00000785 * 1000) / 1000;
  };

  const calcTotalWeight = (dia, len, qty) => {
    return Math.round(calcBilletWeight(dia, len) * (parseFloat(qty) || 0) * 1000) / 1000;
  };

  const calcTotalValue = (dia, len, qty, cost) => {
    return Math.round(calcTotalWeight(dia, len, qty) * (parseFloat(cost) || 0) * 100) / 100;
  };
  
  // Stock Items state
  const [stockItems, setStockItems] = useState([]);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockAdjustDialogOpen, setStockAdjustDialogOpen] = useState(false);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [stockAdjustmentAmount, setStockAdjustmentAmount] = useState('');
  const [editingStock, setEditingStock] = useState(null);
  const [stockFormData, setStockFormData] = useState({
    part_number: '',
    description: '',
    customer: '',
    material: '',
    category: 'Finished Goods',
    location: '',
    quantity_on_hand: '',
    unit: 'pcs',
    minimum_stock: '',
    reorder_quantity: '',
    unit_cost: '',
    supplier: '',
    job_card_number: '',
    notes: ''
  });
  
  // Parts list for dropdown
  const [partsList, setPartsList] = useState([]);
  const [partSearchOpen, setPartSearchOpen] = useState(false);
  const [partSearchQuery, setPartSearchQuery] = useState('');
  const [stockSortBy, setStockSortBy] = useState('part_number');
  
  // Raw material filters
  const [gradeFilter, setGradeFilter] = useState('');
  const [diameterFilter, setDiameterFilter] = useState('');

  // Book to WO modal state
  const [bookToWOOpen, setBookToWOOpen] = useState(false);
  const [bookingItem, setBookingItem] = useState(null);

  // Stock item filters
  const [stockPartFilter, setStockPartFilter] = useState('');
  const [stockCustomerFilter, setStockCustomerFilter] = useState('');
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, stockRes, partsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/inventory`),
        axios.get(`${BACKEND_URL}/api/stock-items`),
        axios.get(`${BACKEND_URL}/api/parts`)
      ]);
      // Auto-backfill inventory_ids for legacy items missing them
      const needsBackfill = invRes.data.some(item => !item.inventory_id);
      if (needsBackfill) {
        await axios.post(`${BACKEND_URL}/api/inventory/backfill-ids`);
        const refreshed = await axios.get(`${BACKEND_URL}/api/inventory`);
        setInventory(refreshed.data);
      } else {
        setInventory(invRes.data);
      }
      setStockItems(stockRes.data);
      setPartsList(partsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  // ===== RAW MATERIALS HANDLERS =====
  const handleRawSubmit = async (e) => {
    e.preventDefault();
    if (!rawFormData.material_grade || !rawFormData.diameter || !rawFormData.quantity_available) {
      toast.error('Please fill in all required fields');
      return;
    }

    const payload = {
      ...rawFormData,
      diameter: parseFloat(rawFormData.diameter),
      length: parseFloat(rawFormData.length) || 0,
      unit_cost: parseFloat(rawFormData.unit_cost) || 0,
      billet_weight: calcBilletWeight(rawFormData.diameter, rawFormData.length),
      total_weight: calcTotalWeight(rawFormData.diameter, rawFormData.length, rawFormData.quantity_available),
      total_value: calcTotalValue(rawFormData.diameter, rawFormData.length, rawFormData.quantity_available, rawFormData.unit_cost),
      quantity_available: parseFloat(rawFormData.quantity_available)
    };

    setSubmitting(true);
    try {
      if (editingRaw) {
        if (!editingRaw.inventory_id) {
          toast.error('Cannot update — item has no ID. Please refresh the page and try again.');
          setSubmitting(false);
          return;
        }
        await axios.put(`${BACKEND_URL}/api/inventory/${editingRaw.inventory_id}`, payload);
        toast.success('Raw material updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/inventory`, payload);
        toast.success('Raw material added');
      }
      setRawDialogOpen(false);
      setEditingRaw(null);
      setRawFormData({ material_grade: '', diameter: '', length: '', cast_number: '', unit_cost: '', heat_treatment_condition: 'Raw', quantity_available: '', unit: 'bars' });
      fetchData();
    } catch (error) {
      toast.error(editingRaw ? 'Failed to update' : 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRawEdit = (item) => {
    setEditingRaw(item);
    setRawFormData({
      material_grade: item.material_grade || '',
      diameter: item.diameter?.toString() || '',
      length: item.length?.toString() || '',
      cast_number: item.cast_number || '',
      unit_cost: item.unit_cost?.toString() || '',
      heat_treatment_condition: item.heat_treatment_condition || 'Raw',
      quantity_available: item.quantity_available?.toString() || '',
      unit: item.unit || 'bars'
    });
    setRawDialogOpen(true);
  };

  const handleRawDelete = async (item) => {
    if (!item.inventory_id) { toast.error('Cannot delete — item has no ID'); return; }
    if (!window.confirm(`Delete ${item.material_grade} ${item.diameter}mm${item.cast_number ? ` (Cast: ${item.cast_number})` : ''}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/inventory/${item.inventory_id}`);
      toast.success('Item deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleRawAdd = () => {
    setEditingRaw(null);
    setRawFormData({ material_grade: '', diameter: '', length: '', cast_number: '', unit_cost: '', heat_treatment_condition: 'Raw', quantity_available: '', unit: 'bars' });
    setRawDialogOpen(true);
  };

  const handleRawAdjustment = async (isAdd) => {
    if (!adjustmentAmount || !selectedRawItem) return;

    try {
      const adjustment = isAdd ? parseFloat(adjustmentAmount) : -parseFloat(adjustmentAmount);
      await axios.patch(`${BACKEND_URL}/api/inventory/adjust`, null, {
        params: {
          material_grade: selectedRawItem.material_grade,
          diameter: selectedRawItem.diameter,
          heat_treatment_condition: selectedRawItem.heat_treatment_condition,
          adjustment
        }
      });
      toast.success(`Inventory ${isAdd ? 'increased' : 'decreased'} by ${adjustmentAmount}`);
      setAdjustDialogOpen(false);
      setAdjustmentAmount('');
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust inventory');
    }
  };

  // ===== STOCK ITEMS HANDLERS =====
  const handleStockAdd = () => {
    setEditingStock(null);
    setStockFormData({
      part_number: '',
      description: '',
      customer: '',
      material: '',
      category: 'Finished Goods',
      location: '',
      quantity_on_hand: '',
      unit: 'pcs',
      minimum_stock: '',
      reorder_quantity: '',
      unit_cost: '',
      supplier: '',
      job_card_number: '',
      notes: ''
    });
    setPartSearchQuery('');
    setStockDialogOpen(true);
  };

  const handleStockEdit = (item) => {
    setEditingStock(item);
    setStockFormData({
      part_number: item.part_number,
      description: item.description || '',
      customer: item.customer || '',
      material: item.material || '',
      category: item.category || 'Finished Goods',
      location: item.location || '',
      quantity_on_hand: item.quantity_on_hand?.toString() || '',
      unit: item.unit || 'pcs',
      minimum_stock: item.minimum_stock?.toString() || '',
      reorder_quantity: item.reorder_quantity?.toString() || '',
      unit_cost: item.unit_cost?.toString() || '',
      supplier: item.supplier || '',
      job_card_number: item.job_card_number || '',
      notes: item.notes || ''
    });
    setPartSearchQuery('');
    setStockDialogOpen(true);
  };

  const handlePartSelect = (part) => {
    setStockFormData(prev => ({
      ...prev,
      part_number: part.part_number,
      description: part.description || '',
      customer: part.customer || '',
      material: part.material_grade || ''
    }));
    setPartSearchOpen(false);
    setPartSearchQuery('');
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    if (!stockFormData.part_number) {
      toast.error('Part number is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...stockFormData,
        quantity_on_hand: parseFloat(stockFormData.quantity_on_hand) || 0,
        minimum_stock: parseFloat(stockFormData.minimum_stock) || 0,
        reorder_quantity: parseFloat(stockFormData.reorder_quantity) || 0,
        unit_cost: parseFloat(stockFormData.unit_cost) || 0
      };

      if (editingStock) {
        await axios.put(`${BACKEND_URL}/api/stock-items/${editingStock.stock_id}`, payload);
        toast.success('Stock item updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/stock-items`, payload);
        toast.success('Stock item added');
      }
      setStockDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stock item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStockDelete = async (item) => {
    if (!confirm(`Delete stock item "${item.part_number}"?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/stock-items/${item.stock_id}`);
      toast.success('Stock item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete stock item');
    }
  };

  const handleStockAdjustment = async (isAdd) => {
    if (!stockAdjustmentAmount || !selectedStockItem) return;

    try {
      const adjustment = isAdd ? parseFloat(stockAdjustmentAmount) : -parseFloat(stockAdjustmentAmount);
      await axios.patch(`${BACKEND_URL}/api/stock-items/${selectedStockItem.stock_id}/adjust`, null, {
        params: { adjustment }
      });
      toast.success(`Stock ${isAdd ? 'increased' : 'decreased'} by ${stockAdjustmentAmount}`);
      setStockAdjustDialogOpen(false);
      setStockAdjustmentAmount('');
      fetchData();
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  // Filter items based on search
  // Unique values for filter dropdowns
  const uniqueGrades = [...new Set(inventory.map(i => i.material_grade || ''))].sort();
  const uniqueDiameters = [...new Set(inventory.map(i => i.diameter))].sort((a, b) => (a || 0) - (b || 0));

  const filteredRawMaterials = inventory.filter(item => {
    // Grade filter
    if (gradeFilter === '__blank') {
      if (item.material_grade && item.material_grade.trim()) return false;
    } else if (gradeFilter && item.material_grade !== gradeFilter) {
      return false;
    }
    // Diameter filter
    if (diameterFilter === '__blank') {
      if (item.diameter && item.diameter > 0) return false;
    } else if (diameterFilter && item.diameter !== parseFloat(diameterFilter)) {
      return false;
    }
    // Search query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        item.material_grade?.toLowerCase().includes(q) ||
        item.heat_treatment_condition?.toLowerCase().includes(q) ||
        (item.cast_number || '').toLowerCase().includes(q) ||
        item.diameter?.toString().includes(searchQuery)
      );
    }
    return true;
  });

  // Unique values for stock filters
  const uniqueStockParts = [...new Set(stockItems.map(i => i.part_number || ''))].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const uniqueStockCustomers = [...new Set(stockItems.map(i => i.customer || '').filter(Boolean))].sort();

  const filteredStockItems = stockItems.filter(item => {
    // Part number filter
    if (stockPartFilter && item.part_number !== stockPartFilter) return false;
    // Customer filter
    if (stockCustomerFilter && (item.customer || '') !== stockCustomerFilter) return false;
    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        item.part_number?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        item.category?.toLowerCase().includes(q) ||
        (item.customer || '').toLowerCase().includes(q) ||
        (item.material || '').toLowerCase().includes(q) ||
        (item.job_card_number || '').toLowerCase().includes(q) ||
        (item.location || '').toLowerCase().includes(q)
      );
    }
    return true;
  }).sort((a, b) => {
    if (stockSortBy === 'quantity_on_hand' || stockSortBy === 'unit_cost') {
      return (parseFloat(b[stockSortBy]) || 0) - (parseFloat(a[stockSortBy]) || 0);
    }
    const valA = (a[stockSortBy] || '').toString();
    const valB = (b[stockSortBy] || '').toString();
    return valA.localeCompare(valB, undefined, { numeric: true });
  });

  // Stats
  const lowStockItems = stockItems.filter(i => i.quantity_on_hand < i.minimum_stock);

  // ===== EXCEL EXPORT =====
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    if (activeTab === 'raw-materials' || activeTab === 'stock-items') {
      // Raw Materials sheet
      const rawData = inventory.map(item => {
        const wtPerBar = item.billet_weight || calcBilletWeight(item.diameter, item.length);
        const totalWt = item.total_weight || calcTotalWeight(item.diameter, item.length, item.quantity_available);
        const totalVal = item.total_value || calcTotalValue(item.diameter, item.length, item.quantity_available, item.unit_cost);
        return {
          'Material Grade': item.material_grade,
          'Cast Number': item.cast_number || '',
          'Diameter (mm)': item.diameter,
          'Length (mm)': item.length || 0,
          'Quantity': item.quantity_available,
          'Unit': item.unit || 'bars',
          'Weight per Bar (kg)': Math.round(wtPerBar * 1000) / 1000,
          'Total Weight (kg)': Math.round(totalWt * 1000) / 1000,
          'Unit Cost (R/kg)': item.unit_cost || 0,
          'Total Value (R)': Math.round(totalVal * 100) / 100,
          'Heat Treatment': item.heat_treatment_condition || '',
          'Last Updated': item.updated_at ? new Date(item.updated_at).toLocaleDateString('en-GB') : ''
        };
      });
      const rawSheet = XLSX.utils.json_to_sheet(rawData);
      rawSheet['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, rawSheet, 'Raw Materials');

      // Stock Items sheet
      const stockData = stockItems.map(item => ({
        'Part Number': item.part_number,
        'Description': item.description,
        'Category': item.category,
        'Location': item.location || '',
        'Qty On Hand': item.quantity_on_hand,
        'Unit': item.unit || 'pcs',
        'Min Stock': item.minimum_stock || '',
        'Reorder Qty': item.reorder_quantity || '',
        'Unit Cost': item.unit_cost || '',
        'Supplier': item.supplier || '',
        'Status': item.quantity_on_hand < (item.minimum_stock || 0) ? 'LOW STOCK' : 'OK',
        'Notes': item.notes || ''
      }));
      const stockSheet = XLSX.utils.json_to_sheet(stockData);
      stockSheet['!cols'] = [{ wch: 16 }, { wch: 30 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, stockSheet, 'Stock Items');
    }

    const filename = `Inventory_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Inventory exported to Excel');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Honeycomb Background */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-inventory" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
              <path 
                d="M28 0 L56 16.67 L56 50 L28 66.67 L0 50 L0 16.67 Z M28 100 L56 83.33 L56 116.67 L28 133.33 L0 116.67 L0 83.33 Z" 
                fill="none" 
                stroke="rgba(0, 200, 255, 0.5)" 
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-inventory)" />
        </svg>
      </div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/95 backdrop-blur-sm border-b border-cyan-500/20">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-400 hover:text-white" data-testid="back-home-btn">
              <Home className="w-4 h-4 mr-2" /> Home
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                <Package className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">Inventory Manager</h1>
                <p className="text-xs text-slate-500">Raw Materials & Stock Items</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Tab Buttons */}
            <div className="flex bg-slate-800/50 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('raw-materials')}
                className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors flex items-center gap-2 ${
                  activeTab === 'raw-materials' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-raw-materials"
              >
                <CircleDot className="w-3.5 h-3.5" />
                Raw Materials
              </button>
              <button
                onClick={() => setActiveTab('stock-items')}
                className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors flex items-center gap-2 ${
                  activeTab === 'stock-items' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-stock-items"
              >
                <Boxes className="w-3.5 h-3.5" />
                Stock Items
                {lowStockItems.length > 0 && (
                  <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full">{lowStockItems.length}</span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('tool-die')}
                className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors flex items-center gap-2 ${
                  activeTab === 'tool-die' ? 'bg-orange-500/20 text-orange-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-tool-die"
              >
                <Wrench className="w-3.5 h-3.5" />
                Tool and Die
              </button>
              <button
                onClick={() => setActiveTab('machining-consumables')}
                className={`px-4 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors flex items-center gap-2 ${
                  activeTab === 'machining-consumables' ? 'bg-purple-500/20 text-purple-400' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-machining-consumables"
              >
                <Settings className="w-3.5 h-3.5" />
                Machining Consumables
              </button>
            </div>
            
            {/* Search - Hide for Tool and Die (has its own search) */}
            {activeTab !== 'tool-die' && activeTab !== 'machining-consumables' && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="pl-9 w-48 bg-slate-800/50 border-slate-700"
                />
              </div>
            )}
            
            {/* Export & Add Buttons */}
            {activeTab !== 'tool-die' && activeTab !== 'machining-consumables' && (
              <Button onClick={handleExportExcel} variant="outline" className="border-slate-700 text-slate-400 hover:text-white" data-testid="export-inventory-btn">
                <Download className="w-4 h-4 mr-2" /> Export Excel
              </Button>
            )}
            {activeTab === 'raw-materials' ? (
              <Button onClick={handleRawAdd} className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50" data-testid="add-raw-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Raw Material
              </Button>
            ) : activeTab === 'stock-items' ? (
              <Button onClick={handleStockAdd} className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50" data-testid="add-stock-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Stock Item
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-32 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto">

          {/* RAW MATERIALS TAB */}
          {activeTab === 'raw-materials' && (
            <div className="bg-slate-900/60 border border-slate-700 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-white uppercase flex items-center gap-2">
                    <CircleDot className="w-5 h-5 text-cyan-400" />
                    Raw Materials (Bars)
                  </h3>
                  <span className="text-sm text-slate-500">{filteredRawMaterials.length} of {inventory.length} items</span>
                </div>
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3" data-testid="raw-material-filters">
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider">Grade</label>
                    <select
                      value={gradeFilter}
                      onChange={(e) => setGradeFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1.5 min-w-[140px] focus:border-cyan-500"
                      data-testid="grade-filter"
                    >
                      <option value="">All Grades</option>
                      <option value="__blank">⚠ Blank / Missing</option>
                      {uniqueGrades.filter(g => g && g.trim()).map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider">Diameter</label>
                    <select
                      value={diameterFilter}
                      onChange={(e) => setDiameterFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1.5 min-w-[140px] focus:border-cyan-500"
                      data-testid="diameter-filter"
                    >
                      <option value="">All Diameters</option>
                      <option value="__blank">⚠ Zero / Missing</option>
                      {uniqueDiameters.filter(d => d && d > 0).map(d => (
                        <option key={d} value={d}>{d} mm</option>
                      ))}
                    </select>
                  </div>
                  {(gradeFilter || diameterFilter) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setGradeFilter(''); setDiameterFilter(''); }}
                      className="text-slate-400 hover:text-white text-xs"
                      data-testid="clear-filters-btn"
                    >
                      <X className="w-3 h-3 mr-1" /> Clear Filters
                    </Button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="text-left py-3 px-3 text-xs text-slate-400 uppercase">Material Grade</th>
                      <th className="text-left py-3 px-3 text-xs text-slate-400 uppercase">Cast No</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Dia (mm)</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Length (mm)</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Qty</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Wt/Bar (kg)</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Total Wt (kg)</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Cost/kg</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Total Value</th>
                      <th className="text-left py-3 px-3 text-xs text-slate-400 uppercase">Condition</th>
                      <th className="text-right py-3 px-3 text-xs text-slate-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRawMaterials.map((item) => {
                      const wtPerBar = item.billet_weight || calcBilletWeight(item.diameter, item.length);
                      const totalWt = item.total_weight || (wtPerBar * (item.quantity_available || 0));
                      const totalVal = item.total_value || (totalWt * (item.unit_cost || 0));
                      return (
                      <tr key={item.inventory_id || item.material_grade + item.diameter} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="py-3 px-3 text-white font-medium">{item.material_grade}</td>
                        <td className="py-3 px-3 text-purple-400 font-mono text-sm">{item.cast_number || '-'}</td>
                        <td className="py-3 px-3 text-right text-cyan-400 font-mono">{item.diameter}</td>
                        <td className="py-3 px-3 text-right text-cyan-400 font-mono">{item.length || '-'}</td>
                        <td className="py-3 px-3 text-right text-white font-bold font-mono">{item.quantity_available} {item.unit}</td>
                        <td className="py-3 px-3 text-right text-amber-400 font-mono">{wtPerBar > 0 ? wtPerBar.toFixed(3) : '-'}</td>
                        <td className="py-3 px-3 text-right text-amber-400 font-mono font-bold">{totalWt > 0 ? totalWt.toFixed(3) : '-'}</td>
                        <td className="py-3 px-3 text-right text-slate-400 font-mono">{item.unit_cost ? formatCurrency(item.unit_cost) : '-'}</td>
                        <td className="py-3 px-3 text-right text-green-400 font-mono font-bold">{totalVal > 0 ? formatCurrency(totalVal) : '-'}</td>
                        <td className="py-3 px-3">
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-950 text-blue-400 rounded">{item.heat_treatment_condition}</span>
                        </td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => { setBookingItem(item); setBookToWOOpen(true); }}
                              className="h-7 px-2 text-green-400 hover:bg-green-500/10"
                              title="Book to Works Order"
                              data-testid={`book-to-wo-${item.inventory_id}`}
                            >
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleRawEdit(item)}
                              className="h-7 px-2 text-cyan-400 hover:bg-cyan-500/10"
                              title="Edit"
                              data-testid={`edit-raw-${item.inventory_id || idx}`}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => { setSelectedRawItem(item); setAdjustDialogOpen(true); }}
                              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs"
                            >
                              Adjust
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleRawDelete(item)}
                              className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredRawMaterials.length === 0 && (
                <div className="p-8 text-center text-slate-500">No raw materials found</div>
              )}
            </div>
          )}

          {/* STOCK ITEMS TAB */}
          {activeTab === 'stock-items' && (
            <div className="bg-slate-900/60 border border-slate-700 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-white uppercase flex items-center gap-2">
                    <Boxes className="w-5 h-5 text-cyan-400" />
                    Stock Items
                  </h3>
                  <div className="flex items-center gap-3">
                    <Select value={stockSortBy} onValueChange={setStockSortBy}>
                      <SelectTrigger className="w-[160px] h-8 bg-slate-800 border-slate-700 text-xs">
                        <SelectValue placeholder="Sort by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="part_number">Sort: Part Number</SelectItem>
                        <SelectItem value="category">Sort: Category</SelectItem>
                        <SelectItem value="customer">Sort: Customer</SelectItem>
                        <SelectItem value="material">Sort: Material</SelectItem>
                        <SelectItem value="location">Sort: Location</SelectItem>
                        <SelectItem value="quantity_on_hand">Sort: Quantity</SelectItem>
                        <SelectItem value="job_card_number">Sort: Job Card</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-slate-500">{filteredStockItems.length} of {stockItems.length} items</span>
                  </div>
                </div>
                {/* Stock Filters */}
                <div className="flex flex-wrap items-center gap-3" data-testid="stock-item-filters">
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider">Part No</label>
                    <select
                      value={stockPartFilter}
                      onChange={(e) => setStockPartFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1.5 min-w-[160px] focus:border-cyan-500"
                      data-testid="stock-part-filter"
                    >
                      <option value="">All Part Numbers</option>
                      {(stockCustomerFilter
                        ? [...new Set(stockItems.filter(i => (i.customer || '') === stockCustomerFilter).map(i => i.part_number))].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                        : uniqueStockParts
                      ).map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-500 uppercase tracking-wider">Customer</label>
                    <select
                      value={stockCustomerFilter}
                      onChange={(e) => setStockCustomerFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-white text-xs rounded px-2 py-1.5 min-w-[160px] focus:border-cyan-500"
                      data-testid="stock-customer-filter"
                    >
                      <option value="">All Customers</option>
                      {uniqueStockCustomers.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  {(stockPartFilter || stockCustomerFilter) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setStockPartFilter(''); setStockCustomerFilter(''); }}
                      className="text-slate-400 hover:text-white text-xs"
                      data-testid="clear-stock-filters-btn"
                    >
                      <X className="w-3 h-3 mr-1" /> Clear Filters
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Low Stock Warning */}
              {lowStockItems.length > 0 && (
                <div className="mx-4 mt-4 p-3 bg-red-950/30 border border-red-500/30 rounded-lg flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="text-red-300 text-sm">
                    <strong>{lowStockItems.length}</strong> items are below minimum stock level
                  </span>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Part Number</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Description</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Customer</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Material</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Location</th>
                      <th className="text-left py-3 px-4 text-xs text-slate-400 uppercase">Job Card</th>
                      <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase">Qty</th>
                      <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase">Min</th>
                      <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase">Unit Cost</th>
                      <th className="text-right py-3 px-4 text-xs text-slate-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStockItems.map((item) => {
                      const isLowStock = item.quantity_on_hand < item.minimum_stock;
                      return (
                        <tr key={item.stock_id} className={`border-b border-slate-800 hover:bg-slate-800/50 ${isLowStock ? 'bg-red-950/20' : ''}`}>
                          <td className="py-3 px-4 font-mono text-cyan-400 text-sm">{item.part_number}</td>
                          <td className="py-3 px-4 text-white font-medium text-sm max-w-[180px] truncate">{item.description || '-'}</td>
                          <td className="py-3 px-4 text-slate-300 text-sm">{item.customer || '-'}</td>
                          <td className="py-3 px-4 text-slate-400 text-sm">{item.material || '-'}</td>
                          <td className="py-3 px-4 text-slate-400 text-sm">{item.location || '-'}</td>
                          <td className="py-3 px-4 text-amber-400 font-mono text-sm">{item.job_card_number || '-'}</td>
                          <td className={`py-3 px-4 text-right font-bold ${isLowStock ? 'text-red-400' : 'text-white'}`}>
                            {item.quantity_on_hand} {item.unit}
                          </td>
                          <td className="py-3 px-4 text-right text-slate-500">{item.minimum_stock || 0}</td>
                          <td className="py-3 px-4 text-right text-emerald-400">{formatCurrency(item.unit_cost || 0)}</td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleStockEdit(item)}
                                className="text-cyan-400 hover:text-cyan-300 h-8 w-8 p-0"
                                title="Edit Item"
                                data-testid={`edit-stock-${item.stock_id}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => { setSelectedStockItem(item); setStockAdjustDialogOpen(true); }}
                                className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                                title="Adjust Quantity"
                                data-testid={`adjust-stock-${item.stock_id}`}
                              >
                                <Package className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleStockDelete(item)}
                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                title="Delete"
                                data-testid={`delete-stock-${item.stock_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredStockItems.length === 0 && (
                <div className="p-8 text-center text-slate-500">No stock items found</div>
              )}
            </div>
          )}

          {/* Tool and Die Tab Content */}
          {activeTab === 'tool-die' && (
            <ToolAndDie />
          )}
          {activeTab === 'machining-consumables' && (
            <MachiningConsumables />
          )}
        </div>
      </main>

      {/* Add Raw Material Dialog */}
      <Dialog open={rawDialogOpen} onOpenChange={setRawDialogOpen}>
        <DialogContent className="bg-slate-900 border-cyan-500/30 max-w-md" aria-describedby="raw-material-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <CircleDot className="w-5 h-5 text-cyan-400" />
              {editingRaw ? 'Edit Raw Material' : 'Add Raw Material'}
            </DialogTitle>
            <DialogDescription id="raw-material-dialog-desc" className="text-slate-400 text-sm">
              {editingRaw ? 'Update raw material details' : 'Add a new bar stock to inventory'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRawSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Material Grade *</Label>
              <Input
                value={rawFormData.material_grade}
                onChange={(e) => setRawFormData({ ...rawFormData, material_grade: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="e.g., EN8, EN19, 4140"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Diameter (mm) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={rawFormData.diameter}
                  onChange={(e) => setRawFormData({ ...rawFormData, diameter: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="25.0"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Length (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={rawFormData.length}
                  onChange={(e) => setRawFormData({ ...rawFormData, length: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="5800"
                  data-testid="raw-length-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Cast Number</Label>
                <Input
                  value={rawFormData.cast_number}
                  onChange={(e) => setRawFormData({ ...rawFormData, cast_number: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="e.g. CAST-2026-001"
                  data-testid="raw-cast-input"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Unit Cost (R/kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rawFormData.unit_cost}
                  onChange={(e) => setRawFormData({ ...rawFormData, unit_cost: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="0.00"
                  data-testid="raw-cost-input"
                />
              </div>
            </div>
            {calcBilletWeight(rawFormData.diameter, rawFormData.length) > 0 && (
              <div className="bg-amber-950/30 border border-amber-500/20 rounded-lg px-4 py-3 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[10px] text-amber-400/70 uppercase">Weight/Bar</p>
                  <p className="text-sm font-bold font-mono text-amber-400">{calcBilletWeight(rawFormData.diameter, rawFormData.length)} kg</p>
                </div>
                <div>
                  <p className="text-[10px] text-amber-400/70 uppercase">Total Weight</p>
                  <p className="text-sm font-bold font-mono text-amber-400">{calcTotalWeight(rawFormData.diameter, rawFormData.length, rawFormData.quantity_available)} kg</p>
                </div>
                <div>
                  <p className="text-[10px] text-green-400/70 uppercase">Total Value</p>
                  <p className="text-sm font-bold font-mono text-green-400">{formatCurrency(calcTotalValue(rawFormData.diameter, rawFormData.length, rawFormData.quantity_available, rawFormData.unit_cost))}</p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Quantity *</Label>
                <Input
                  type="number"
                  value={rawFormData.quantity_available}
                  onChange={(e) => setRawFormData({ ...rawFormData, quantity_available: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="10"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Heat Treatment Condition</Label>
              <Select value={rawFormData.heat_treatment_condition} onValueChange={(v) => setRawFormData({ ...rawFormData, heat_treatment_condition: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HEAT_TREATMENT_CONDITIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setRawDialogOpen(false); setEditingRaw(null); }} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-cyan-600 hover:bg-cyan-500">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : editingRaw ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {editingRaw ? 'Save Changes' : 'Add Material'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Raw Material Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="bg-slate-900 border-cyan-500/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide">Adjust Quantity</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedRawItem?.material_grade} - Ø{selectedRawItem?.diameter}mm ({selectedRawItem?.heat_treatment_condition})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Current Quantity</p>
              <p className="text-3xl font-bold text-cyan-400">{selectedRawItem?.quantity_available}</p>
              <p className="text-slate-500">{selectedRawItem?.unit || 'bars'}</p>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Adjustment Amount</Label>
              <Input
                type="number"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                className="bg-slate-800 border-slate-700 text-center text-xl"
                placeholder="0"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => handleRawAdjustment(false)} className="flex-1 bg-red-600 hover:bg-red-500" disabled={!adjustmentAmount}>
                <Minus className="w-4 h-4 mr-2" /> Remove
              </Button>
              <Button onClick={() => handleRawAdjustment(true)} className="flex-1 bg-green-600 hover:bg-green-500" disabled={!adjustmentAmount}>
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Stock Item Dialog */}
      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="bg-slate-900 border-cyan-500/30 max-w-lg" aria-describedby="stock-item-dialog-desc">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Boxes className="w-5 h-5 text-cyan-400" />
              {editingStock ? 'Edit Stock Item' : 'Add Stock Item'}
            </DialogTitle>
            <DialogDescription id="stock-item-dialog-desc" className="text-slate-400 text-sm">
              {editingStock ? 'Update stock item details' : 'Add a new finished good or component to inventory'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStockSubmit} className="space-y-4 max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-slate-400 uppercase text-xs">Part Number * (from master list)</Label>
                <div className="relative">
                  <div
                    onClick={() => setPartSearchOpen(!partSearchOpen)}
                    className="flex items-center justify-between bg-slate-800 border border-slate-700 rounded-md px-3 py-2 cursor-pointer hover:border-cyan-500/50"
                    data-testid="stock-part-select"
                  >
                    <span className={stockFormData.part_number ? 'text-white font-mono' : 'text-slate-500'}>
                      {stockFormData.part_number || 'Select part number...'}
                    </span>
                    <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${partSearchOpen ? 'rotate-90' : ''}`} />
                  </div>
                  {partSearchOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl max-h-[200px] overflow-y-auto">
                      <div className="p-2 border-b border-slate-800">
                        <Input
                          value={partSearchQuery}
                          onChange={(e) => setPartSearchQuery(e.target.value)}
                          placeholder="Search parts..."
                          className="bg-slate-800 border-slate-700 text-sm h-8"
                          autoFocus
                        />
                      </div>
                      {partsList
                        .filter(p => {
                          if (!partSearchQuery) return true;
                          const q = partSearchQuery.toLowerCase();
                          return p.part_number?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q) || (p.customer || '').toLowerCase().includes(q);
                        })
                        .map(part => (
                          <div
                            key={part.part_number}
                            onClick={() => handlePartSelect(part)}
                            className="px-3 py-2 hover:bg-cyan-500/10 cursor-pointer border-b border-slate-800/50"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-cyan-400 font-mono text-sm font-bold">{part.part_number}</span>
                              {part.customer && <span className="text-xs text-slate-500">{part.customer}</span>}
                            </div>
                            <p className="text-xs text-slate-400 truncate">{part.description}</p>
                          </div>
                        ))
                      }
                      {partsList.filter(p => !partSearchQuery || p.part_number?.toLowerCase().includes(partSearchQuery.toLowerCase()) || p.description?.toLowerCase().includes(partSearchQuery.toLowerCase())).length === 0 && (
                        <div className="px-3 py-3 text-xs text-slate-600 text-center">No parts found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-slate-400 uppercase text-xs">Description</Label>
                <Input value={stockFormData.description} onChange={(e) => setStockFormData({ ...stockFormData, description: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="Auto-filled from part" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Customer</Label>
                <Input value={stockFormData.customer} onChange={(e) => setStockFormData({ ...stockFormData, customer: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="Auto-filled from part" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Material</Label>
                <Input value={stockFormData.material} onChange={(e) => setStockFormData({ ...stockFormData, material: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="Auto-filled from part" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Category</Label>
                <Select value={stockFormData.category} onValueChange={(v) => setStockFormData({ ...stockFormData, category: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STOCK_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Produced on Job Card No</Label>
                <Input value={stockFormData.job_card_number} onChange={(e) => setStockFormData({ ...stockFormData, job_card_number: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="e.g. WO-12345" data-testid="stock-job-card" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Quantity</Label>
                <Input type="number" value={stockFormData.quantity_on_hand} onChange={(e) => setStockFormData({ ...stockFormData, quantity_on_hand: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="0" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Min Stock</Label>
                <Input type="number" value={stockFormData.minimum_stock} onChange={(e) => setStockFormData({ ...stockFormData, minimum_stock: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="0" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Unit</Label>
                <Select value={stockFormData.unit} onValueChange={(v) => setStockFormData({ ...stockFormData, unit: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STOCK_UNITS.map((u) => (<SelectItem key={u} value={u}>{u}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Location</Label>
                <Input value={stockFormData.location} onChange={(e) => setStockFormData({ ...stockFormData, location: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="e.g., Shelf A-1" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Unit Cost (R)</Label>
                <Input type="number" step="0.01" value={stockFormData.unit_cost} onChange={(e) => setStockFormData({ ...stockFormData, unit_cost: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="0.00" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Supplier</Label>
                <Input value={stockFormData.supplier} onChange={(e) => setStockFormData({ ...stockFormData, supplier: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="Supplier name" data-testid="stock-supplier" />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Reorder Qty</Label>
                <Input type="number" value={stockFormData.reorder_quantity} onChange={(e) => setStockFormData({ ...stockFormData, reorder_quantity: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="0" data-testid="stock-reorder-qty" />
              </div>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Notes</Label>
              <Textarea value={stockFormData.notes} onChange={(e) => setStockFormData({ ...stockFormData, notes: e.target.value })} className="bg-slate-800 border-slate-700 min-h-[40px]" placeholder="Additional notes..." />
            </div>
            <div className="flex justify-between gap-3 pt-4">
              {editingStock ? (
                <Button type="button" variant="ghost" onClick={() => { setStockDialogOpen(false); handleStockDelete(editingStock); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="delete-in-edit-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Item
                </Button>
              ) : <div />}
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStockDialogOpen(false)} className="border-slate-600">Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-cyan-600 hover:bg-cyan-500">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingStock ? 'Update' : 'Add'} Item
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Item Dialog */}
      <Dialog open={stockAdjustDialogOpen} onOpenChange={setStockAdjustDialogOpen}>
        <DialogContent className="bg-slate-900 border-cyan-500/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide">Adjust Stock Quantity</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedStockItem?.part_number} - {selectedStockItem?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-4 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-500 uppercase">Current Quantity</p>
              <p className="text-3xl font-bold text-cyan-400">{selectedStockItem?.quantity_on_hand}</p>
              <p className="text-slate-500">{selectedStockItem?.unit || 'pcs'}</p>
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Adjustment Amount</Label>
              <Input
                type="number"
                value={stockAdjustmentAmount}
                onChange={(e) => setStockAdjustmentAmount(e.target.value)}
                className="bg-slate-800 border-slate-700 text-center text-xl"
                placeholder="0"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => handleStockAdjustment(false)} className="flex-1 bg-red-600 hover:bg-red-500" disabled={!stockAdjustmentAmount}>
                <Minus className="w-4 h-4 mr-2" /> Remove
              </Button>
              <Button onClick={() => handleStockAdjustment(true)} className="flex-1 bg-green-600 hover:bg-green-500" disabled={!stockAdjustmentAmount}>
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Book to Works Order Modal */}
      <BookToWOModal
        open={bookToWOOpen}
        onOpenChange={setBookToWOOpen}
        inventoryItem={bookingItem}
        onSuccess={fetchData}
        user={user}
      />
    </div>
  );
};

export default Inventory;
